import { offlineDb, compareZoneOrder } from "@/core/offline/db";
import type {
  OfflineSite,
  OfflineZone,
  OfflineCampagne,
  OfflinePin,
  OfflineMesure,
  OfflinePhoto,
} from "@/core/offline/db";

// ─── Types export (modèle B) ─────────────────────────────────

export interface SiteExportData {
  site: OfflineSite;
  campagne: OfflineCampagne; // La campagne exportée (snapshot à date)
  zones: ZoneExportData[];   // Zones du site avec pins regroupés par zone
}

export interface ZoneExportData {
  zone: OfflineZone;
  pins: PinExportData[];
}

export interface PinExportData {
  pin: OfflinePin;
  mesure: OfflineMesure | null; // Mesure pour (pin, campagne)
  photos: OfflinePhoto[];
}

// ─── Data fetcher ────────────────────────────────────────────

/**
 * Charge les données d'un site pour l'export, à la date d'une campagne donnée.
 *
 * - Si `campagneId` est fourni : exporte ce snapshot
 * - Sinon : prend la campagne active, ou la plus récente campagne clôturée
 *
 * Modèle B : les pins sont au niveau site (persistants), regroupés par zone
 * pour l'affichage. Les mesures correspondent à la campagne sélectionnée.
 */
export async function fetchSiteExportData(
  siteId: string,
  campagneId?: string
): Promise<SiteExportData | null> {
  const site = await offlineDb.sites.get(siteId);
  if (!site) return null;

  // Déterminer la campagne à exporter
  const allCampagnes = await offlineDb.campagnes
    .where("siteId")
    .equals(siteId)
    .sortBy("date");

  let campagne: OfflineCampagne | undefined;
  if (campagneId) {
    campagne = allCampagnes.find((c) => c.id === campagneId);
  } else {
    // Défaut : active > plus récente clôturée
    campagne = allCampagnes.find((c) => c.status === "active")
      ?? allCampagnes[allCampagnes.length - 1];
  }
  if (!campagne) return null; // Site sans aucune campagne, pas exportable

  // Charger les zones du site.
  // Ordre = même que la page site-detail (useZones) : order asc, défini par
  // Renaud via drag-and-drop (tiebreaker createdAt asc). L'export PDF respecte
  // donc l'ordre de la prise de mesure.
  const zones = (await offlineDb.zones
    .where("siteId")
    .equals(siteId)
    .toArray())
    .sort(compareZoneOrder);

  // Charger tous les pins du site (on filtrera par zone)
  const allSitePins = await offlineDb.pins
    .where("siteId")
    .equals(siteId)
    .toArray();

  // Charger toutes les mesures de la campagne pour ce site
  const campagneMesures = await offlineDb.mesures
    .where("campagneId")
    .equals(campagne.id)
    .toArray();
  const mesureByPinId = new Map<string, OfflineMesure>();
  for (const m of campagneMesures) {
    mesureByPinId.set(m.pinId, m);
  }

  const zonesData: ZoneExportData[] = [];

  for (const zone of zones) {
    const zonePins = allSitePins.filter((p) => p.zoneId === zone.id);
    const pinsData: PinExportData[] = [];

    for (const pin of zonePins) {
      const mesure = mesureByPinId.get(pin.id) ?? null;

      const photos = mesure
        ? await offlineDb.photos
            .where("mesureId")
            .equals(mesure.id)
            .toArray()
        : [];

      pinsData.push({ pin, mesure, photos });
    }

    // Trier les pins par numéro
    pinsData.sort((a, b) => a.pin.number - b.pin.number);

    zonesData.push({ zone, pins: pinsData });
  }

  return { site, campagne, zones: zonesData };
}

// ─── Campagne précédente pour comparatif N vs N-X ─────────────

export interface PreviousCampagneData {
  campagne: OfflineCampagne;
  mesureByPinId: Map<string, OfflineMesure>;
}

/**
 * Cherche la campagne immédiatement antérieure à la campagne donnée pour le
 * site, et charge ses mesures par pinId.
 *
 * Sert au tableau de synthèse PDF : si une campagne précédente existe, on
 * ajoute les colonnes "% N-X" et "Évolution" pour visualiser la dégradation
 * (ou amélioration) dans le temps.
 *
 * Retourne null s'il n'existe aucune campagne antérieure (1ère visite).
 */
export async function fetchPreviousCampagneMesures(
  siteId: string,
  currentCampagne: OfflineCampagne
): Promise<PreviousCampagneData | null> {
  const allCampagnes = await offlineDb.campagnes
    .where("siteId")
    .equals(siteId)
    .sortBy("date");

  const currentDate = currentCampagne.date.getTime();
  // Plus récente campagne strictement antérieure à la courante
  const previous = [...allCampagnes]
    .filter((c) => c.id !== currentCampagne.id && c.date.getTime() < currentDate)
    .sort((a, b) => b.date.getTime() - a.date.getTime())[0];

  if (!previous) return null;

  const previousMesures = await offlineDb.mesures
    .where("campagneId")
    .equals(previous.id)
    .toArray();

  const mesureByPinId = new Map<string, OfflineMesure>();
  for (const m of previousMesures) {
    mesureByPinId.set(m.pinId, m);
  }

  return { campagne: previous, mesureByPinId };
}
